import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  SchedulerQueuesResponse,
  SchedulerTasksResponse,
  SchedulerWorkersResponse,
  ValidatorKind,
  ValidatorStuckParams,
  ValidatorStuckResponse,
  ValidatorVerdictsParams,
  ValidatorVerdictsResponse,
  WebhookRoute,
  WebhooksListResponse,
  WorkflowDetailResponse,
  WorkflowExecutionDetailResponse,
  WorkflowExecutionsParams,
  WorkflowExecutionsResponse,
  WorkflowListParams,
  WorkflowListResponse,
  WorkflowStatsResponse,
} from '@/client/types/workflows';

const BASE = '/api/admin/v2/workflows';

export const workflowKeys = {
  all: ['workflows-v2'] as const,
  list: (p: object) => [...workflowKeys.all, 'list', p] as const,
  detail: (wid: string) => [...workflowKeys.all, 'detail', wid] as const,
  executions: (wid: string, p: object) => [...workflowKeys.all, 'executions', wid, p] as const,
  execution: (wid: string, eid: string) => [...workflowKeys.all, 'execution', wid, eid] as const,
  stats: (wid: string, hours: number) => [...workflowKeys.all, 'stats', wid, hours] as const,
  schedulerTasks: () => [...workflowKeys.all, 'scheduler', 'tasks'] as const,
  schedulerQueues: (queues?: string) => [...workflowKeys.all, 'scheduler', 'queues', queues ?? ''] as const,
  schedulerWorkers: (timeout?: number) =>
    [...workflowKeys.all, 'scheduler', 'workers', timeout ?? 3] as const,
  verdicts: (kind: ValidatorKind, p: object) =>
    [...workflowKeys.all, 'validator', kind, 'verdicts', p] as const,
  catches: (kind: ValidatorKind, p: object) =>
    [...workflowKeys.all, 'validator', kind, 'catches', p] as const,
  stuck: (kind: ValidatorKind, p: object) =>
    [...workflowKeys.all, 'validator', kind, 'stuck', p] as const,
  webhooks: () => [...workflowKeys.all, 'webhooks'] as const,
  webhook: (route: string) => [...workflowKeys.all, 'webhooks', route] as const,
};

const qs = (raw: Record<string, unknown>) => {
  const params = Object.entries(raw).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const s = new URLSearchParams(params).toString();
  return s ? `?${s}` : '';
};

/**
 * Admin v2 usually wraps payloads in `{ status, data }`. The workflows guide
 * documents the inner payload — accept either shape.
 */
export const unwrapWorkflows = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as APIResponse<T>).data as T;
  }
  return raw as T;
};

// ─── n8n ──────────────────────────────────────────────────────────────────────

export const useWorkflowListQuery = (params: WorkflowListParams = {}, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.list(params),
    queryFn: withErrorHandling(async (): Promise<WorkflowListResponse> => {
      const raw = await request<APIResponse<WorkflowListResponse> | WorkflowListResponse>(
        `${BASE}${qs(params as Record<string, unknown>)}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load workflows'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useWorkflowDetailQuery = (wid: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.detail(wid ?? ''),
    queryFn: withErrorHandling(async (): Promise<WorkflowDetailResponse> => {
      const raw = await request<APIResponse<WorkflowDetailResponse> | WorkflowDetailResponse>(
        `${BASE}/${wid}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load workflow'),
    enabled: !!wid && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useWorkflowExecutionsQuery = (
  wid: string | null,
  params: WorkflowExecutionsParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.executions(wid ?? '', params),
    queryFn: withErrorHandling(async (): Promise<WorkflowExecutionsResponse> => {
      const raw = await request<
        APIResponse<WorkflowExecutionsResponse> | WorkflowExecutionsResponse
      >(`${BASE}/${wid}/executions${qs(params as Record<string, unknown>)}`);
      return unwrapWorkflows(raw);
    }, 'Failed to load executions'),
    enabled: !!wid && enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useWorkflowExecutionQuery = (
  wid: string | null,
  executionId: string | null,
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.execution(wid ?? '', executionId ?? ''),
    queryFn: withErrorHandling(async (): Promise<WorkflowExecutionDetailResponse> => {
      const raw = await request<
        APIResponse<WorkflowExecutionDetailResponse> | WorkflowExecutionDetailResponse
      >(`${BASE}/${wid}/executions/${executionId}`);
      return unwrapWorkflows(raw);
    }, 'Failed to load execution'),
    enabled: !!wid && !!executionId && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useWorkflowStatsQuery = (wid: string | null, hours = 24, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.stats(wid ?? '', hours),
    queryFn: withErrorHandling(async (): Promise<WorkflowStatsResponse> => {
      const raw = await request<APIResponse<WorkflowStatsResponse> | WorkflowStatsResponse>(
        `${BASE}/${wid}/stats?hours=${hours}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load workflow stats'),
    enabled: !!wid && enabled,
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Scheduler ────────────────────────────────────────────────────────────────

export const useSchedulerTasksQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.schedulerTasks(),
    queryFn: withErrorHandling(async (): Promise<SchedulerTasksResponse> => {
      const raw = await request<APIResponse<SchedulerTasksResponse> | SchedulerTasksResponse>(
        `${BASE}/scheduler/tasks`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load scheduler tasks'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useSchedulerQueuesQuery = (queues?: string, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.schedulerQueues(queues),
    queryFn: withErrorHandling(async (): Promise<SchedulerQueuesResponse> => {
      const raw = await request<APIResponse<SchedulerQueuesResponse> | SchedulerQueuesResponse>(
        `${BASE}/scheduler/queues${qs({ queues })}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load queue depths'),
    enabled,
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
    refetchInterval: 15_000,
  });
};

export const useSchedulerWorkersQuery = (timeout = 3, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.schedulerWorkers(timeout),
    queryFn: withErrorHandling(async (): Promise<SchedulerWorkersResponse> => {
      const raw = await request<APIResponse<SchedulerWorkersResponse> | SchedulerWorkersResponse>(
        `${BASE}/scheduler/workers?timeout=${timeout}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load workers'),
    enabled,
    staleTime: 10_000,
    gcTime: DEFAULT_GC_TIME,
    refetchInterval: 20_000,
  });
};

// ─── Validator ────────────────────────────────────────────────────────────────

export const useValidatorVerdictsQuery = (
  kind: ValidatorKind,
  params: ValidatorVerdictsParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.verdicts(kind, params),
    queryFn: withErrorHandling(async (): Promise<ValidatorVerdictsResponse> => {
      const raw = await request<
        APIResponse<ValidatorVerdictsResponse> | ValidatorVerdictsResponse
      >(`${BASE}/validator/${kind}/verdicts${qs(params as Record<string, unknown>)}`);
      return unwrapWorkflows(raw);
    }, 'Failed to load verdicts'),
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useValidatorCatchesQuery = (
  kind: ValidatorKind,
  params: ValidatorVerdictsParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  const withDefaults = { since_hours: 168, ...params };
  return useQuery({
    queryKey: workflowKeys.catches(kind, withDefaults),
    queryFn: withErrorHandling(async (): Promise<ValidatorVerdictsResponse> => {
      const raw = await request<
        APIResponse<ValidatorVerdictsResponse> | ValidatorVerdictsResponse
      >(`${BASE}/validator/${kind}/catches${qs(withDefaults as Record<string, unknown>)}`);
      return unwrapWorkflows(raw);
    }, 'Failed to load safety catches'),
    enabled,
    staleTime: 30_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useValidatorStuckQuery = (
  kind: ValidatorKind,
  params: ValidatorStuckParams = {},
  enabled = true,
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.stuck(kind, params),
    queryFn: withErrorHandling(async (): Promise<ValidatorStuckResponse> => {
      const raw = await request<APIResponse<ValidatorStuckResponse> | ValidatorStuckResponse>(
        `${BASE}/validator/${kind}/stuck${qs(params as Record<string, unknown>)}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load stuck validations'),
    enabled,
    staleTime: 15_000,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

// ─── Webhooks ─────────────────────────────────────────────────────────────────

export const useWebhooksQuery = (enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.webhooks(),
    queryFn: withErrorHandling(async (): Promise<WebhooksListResponse> => {
      const raw = await request<APIResponse<WebhooksListResponse> | WebhooksListResponse>(
        `${BASE}/webhooks`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load webhook routes'),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useWebhookRouteQuery = (route: string | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: workflowKeys.webhook(route ?? ''),
    queryFn: withErrorHandling(async (): Promise<WebhookRoute> => {
      const raw = await request<APIResponse<WebhookRoute> | WebhookRoute>(
        `${BASE}/webhooks/${route}`,
      );
      return unwrapWorkflows(raw);
    }, 'Failed to load webhook route'),
    enabled: !!route && enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};
